


import { Component, Prop, Vue } from "vue-property-decorator";

import BooleanField from "Web/Components/Common/Input/Boolean.vue";
import SimpleSelect from "Web/Components/V2/Select/Simple.vue";
import { Option } from "Web/Contracts";
import { CustomValue } from "@Internal/Models";

import { AdsPublications, Customers } from "App";
import { $t, EventBus } from "App/IOC";
import { CustomNotification, PagerSorter } from "App/Models";

interface GroupOption {
    CustomerId: number;
    GroupId: number;
    Label: string;
}

interface UserOption {
    Id: number;
    Label: string;
}

/* Per-user setups for a single portal (Amanda). A customer is a Console client attached to a
   groupId; from it we reach the group's users and configure each one's activation of the portal
   (code / credentials / auto broadcast). The portal's attributed customers give the group list; the
   group endpoints (list/update/delete) are scoped by groupId, create by the chosen user. */
@Component( {
    components: { BooleanField, SimpleSelect }
} )
export default class SetupGatewayPanel extends Vue {
    @Prop( { required: true } ) readonly gatewayId!: number;
    @Prop( { default: "" } ) readonly gatewayName!: string;

    private groups: GroupOption[] = [];
    private selectedGroupId: number = 0;
    private selectedCustomerId: number = 0;

    private groupUsers: UserOption[] = [];

    private rows: any[] = [];
    private loading: boolean = false;

    private selectedUserId: number = 0;
    private editingId: number = 0;
    private formCode: string = "";
    private formUsername: string = "";
    private formPassword: string = "";
    private formAuto: boolean = false;

    private async created(): Promise<void> {
        await this.loadGroups();
    }

    /* The relevant groups are the ones the portal is attributed to (its customer list). Each
       customer carries its own id and the groupId it is attached to. */
    private async loadGroups(): Promise<void> {
        const [ customers, , error ] =
            await AdsPublications.GetAllCustomers( [], new PagerSorter( 999 ) );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        const seen: Record<string, boolean> = {};
        const options: GroupOption[] = [];

        for( const customer of customers as any[] ) {
            const groupId = Number( customer.Group );
            if( !groupId || seen[ groupId ] )
                continue;

            seen[ groupId ] = true;
            options.push( {
                CustomerId: Number( customer.Id ),
                GroupId: groupId,
                Label: customer.Company || customer.Owner || customer.Name || String( groupId )
            } );
        }

        this.groups = options;

        if( options.length > 0 ) {
            this.selectedGroupId = options[ 0 ].GroupId;
            this.selectedCustomerId = options[ 0 ].CustomerId;
            await Promise.all( [ this.loadSetups(), this.loadGroupUsers() ] );
        }
    }

    private async onGroupChange(): Promise<void> {
        const group = this.groups.find( option => option.GroupId === this.selectedGroupId );
        this.selectedCustomerId = group !== undefined ? group.CustomerId : 0;

        this.resetForm();
        await Promise.all( [ this.loadSetups(), this.loadGroupUsers() ] );
    }

    private async loadSetups(): Promise<void> {
        if( !this.selectedGroupId )
            return;

        this.loading = true;

        const [ setups, error ] = await AdsPublications.SetupList( this.selectedGroupId );

        this.loading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        /* The list is group-wide (every portal), so keep only the setups for this portal. */
        this.rows = setups.filter(
            ( setup: any ) => !setup.GatewayId || setup.GatewayId === this.gatewayId
        );
    }

    /* The group's users are reached through the selected customer (customer -> group -> users). */
    private async loadGroupUsers(): Promise<void> {
        if( !this.selectedCustomerId ) {
            this.groupUsers = [];
            return;
        }

        const [ users, error ] = await Customers.ListGroupUsers( this.selectedCustomerId );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.groupUsers = users.map( ( user: any ) => ( {
            Id: user.Id,
            Label: user.Company ? `${ user.Name } — ${ user.Company }` : user.Name
        } ) );
    }

    /* Groupe et utilisateur au composant du DS ( 26/09/2026 ). */
    private get groupOptions(): Option[] {
        return this.groups.map(
            ( group ): Option => new CustomValue<number>( group.Label, group.GroupId )
        );
    }

    private get selectedGroup(): Option | undefined {
        return this.groupOptions.find( ( option: Option ) => option.Id === this.selectedGroupId );
    }

    private selectGroup( option: Option ): void {
        this.selectedGroupId = Number( option.Id );
        this.onGroupChange();
    }

    private get userOptions(): Option[] {
        return this.groupUsers.map(
            ( user ): Option => new CustomValue<number>( user.Label, user.Id )
        );
    }

    private get selectedUser(): Option | undefined {
        return this.userOptions.find( ( option: Option ) => option.Id === this.selectedUserId );
    }

    private selectUser( option: Option ): void {
        this.selectedUserId = Number( option.Id );
    }

    private get isEditing(): boolean {
        return this.editingId !== 0;
    }

    private startEdit( row: any ): void {
        this.editingId = row.Id;
        this.selectedUserId = row.UserId;
        this.formCode = row.Code;
        this.formUsername = row.Username;
        this.formPassword = "";
        this.formAuto = row.Auto === true;
    }

    private resetForm(): void {
        this.editingId = 0;
        this.selectedUserId = 0;
        this.formCode = "";
        this.formUsername = "";
        this.formPassword = "";
        this.formAuto = false;
    }

    private onAutoChange( value: boolean ): void {
        this.formAuto = value;
    }

    private async submit(): Promise<void> {
        if( this.isEditing ) {
            await this.submitUpdate();
            return;
        }

        await this.submitCreate();
    }

    private async submitCreate(): Promise<void> {
        if( !this.selectedUserId ) {
            EventBus.Publish( "Error", { Message: $t( "SETUP_GATEWAY_errorNoUser" ) } );
            return;
        }

        const [ , error ] = await AdsPublications.SetupCreate( {
            UserId: this.selectedUserId,
            GatewayId: this.gatewayId,
            Code: this.formCode,
            Username: this.formUsername,
            Password: this.formPassword,
            Auto: this.formAuto
        } );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notifySaved();
        this.resetForm();
        await this.loadSetups();
    }

    private async submitUpdate(): Promise<void> {
        const [ , error ] = await AdsPublications.SetupUpdate(
            this.editingId,
            this.selectedGroupId,
            {
                GatewayId: this.gatewayId,
                Code: this.formCode,
                Username: this.formUsername,
                Password: this.formPassword,
                Auto: this.formAuto
            }
        );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notifySaved();
        this.resetForm();
        await this.loadSetups();
    }

    private remove( row: any ): void {
        EventBus.Publish( "Confirmation", {
            Props: {
                Title: this.$t( "GLOBAL_attention" ),
                Message: this.$t( "SETUP_GATEWAY_deleteConfirm" ),
                Actions: [ {
                    Id: "delete",
                    Name: this.$t( "GLOBAL_actionDelete" ),
                    Active: true
                } ]
            },
            Events: {
                Action: async () => {
                    const [ , error ] =
                        await AdsPublications.SetupDelete( row.Id, this.selectedGroupId );

                    if( error !== undefined ) {
                        EventBus.Publish( "Error", error );
                        return;
                    }

                    this.notifySaved();
                    await this.loadSetups();
                }
            }
        } );
    }

    private userLabel( id: number ): string {
        const user = this.groupUsers.find( option => option.Id === id );
        return user !== undefined ? user.Label : String( id );
    }

    private notifySaved(): void {
        EventBus.Publish(
            "Notification",
            CustomNotification.Success(
                $t( "ADS_PUBLICATION_createSuccessTitle" ),
                $t( "ADS_PUBLICATION_createUpdateMessage" )
            )
        );
    }
}
